import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  coveragePdfUrl,
  getCoverage,
  listProjects,
  runTestsForCrawl,
  waitForRunFinish,
  type CoverageKpis,
  type CoveragePage as CoveragePageRow,
  type CoverageReportResponse,
  type ProjectSummary,
} from "../lib/api";
import { FlowGraphSvg, type CoverageNodeStatus } from "../components/FlowGraphSvg";
import { useAuth } from "../lib/auth";

/**
 * Coverage Map page — Plan Journey 5 "Coverage Audit".
 *
 * "A VP asks for a coverage report. Priya clicks 'Coverage Map.' UAIP overlays
 * the site graph with test coverage in green, gaps in red. 68% of pages have
 * at least one test; 23% have critical flows tested; 9% are completely
 * untested."
 *
 * The page is route-driven: `/projects/:id/coverage`. Users without a project
 * id hit `/coverage`, which auto-redirects to the first project or shows an
 * empty state if they have none yet.
 *
 * v1 layout:
 *   - 3 KPI tiles at the top matching the Journey-5 numbers
 *   - critical-flows rail (login / signup / cart / checkout / payment)
 *   - full page list with per-page status badges
 *   - window picker (?days=7 by default, 1 / 7 / 30 / 90)
 *
 * Deliberately v1-only — no SVG heatmap overlay on the flow graph yet. The
 * flow-graph overlay lands next sprint once we add coverage-by-URL keyed
 * into @uaip/flow-graph's FlowNode type. For now, the per-page status table
 * plus the critical rail cover Journey 5's "% pages tested" + "% critical
 * flows tested" + "% untested" call-outs cleanly.
 */
export function CoveragePage(): JSX.Element {
  const { id: projectIdParam } = useParams<{ id: string }>();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const { user, ready } = useAuth();
  const daysParam = search.get("days");
  const days = daysParam ? clampDays(Number(daysParam)) : 7;

  const [projectId, setProjectId] = useState<string | null>(
    projectIdParam ?? null,
  );
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [coverage, setCoverage] = useState<CoverageReportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Always load the project list — we need it for the picker even when :id
  // is already in the URL. When landing on /coverage with no :id, auto-select
  // the first project to avoid an awkward "pick a project" first screen.
  useEffect(() => {
    // Coverage is user-scoped; the signed-out gate below renders instead, so
    // don't fire a fetch that would just 401 while we're anonymous/resolving.
    if (!ready || !user) return;
    let cancelled = false;
    listProjects()
      .then((list) => {
        if (cancelled) return;
        setProjects(list);
        if (!projectIdParam && list.length > 0 && !projectId) {
          setProjectId(list[0]!.id);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
    // Re-run only when the URL changes routes; the auto-select check above
    // handles the "second visit with no :id" case idempotently.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectIdParam, ready, user]);

  // Keep `projectId` in sync with the URL param when it changes
  // (e.g. navigating from the picker).
  useEffect(() => {
    if (projectIdParam && projectIdParam !== projectId) {
      setProjectId(projectIdParam);
    }
  }, [projectIdParam, projectId]);

  /** Navigate to a different project, preserving the window query param. */
  const switchProject = (id: string): void => {
    const qs = daysParam ? `?days=${daysParam}` : "";
    navigate(`/projects/${encodeURIComponent(id)}/coverage${qs}`);
  };

  useEffect(() => {
    if (!ready || !user || !projectId) return;
    let cancelled = false;
    setCoverage(null);
    setError(null);
    getCoverage(projectId, days)
      .then((r) => {
        if (!cancelled) setCoverage(r);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, days, ready, user]);

  const pickDays = (n: number): void => {
    // Keep the search-param wire format minimal (omit when default).
    if (n === 7) search.delete("days");
    else search.set("days", String(n));
    setSearch(search, { replace: true });
  };

  // Plan §21 Phase 4 tag filter — `?tag=critical-path` narrows the map
  // and page list to pages carrying that tag. Empty / unset = "all".
  const activeTag = search.get("tag") ?? "";
  const pickTag = (tag: string): void => {
    if (!tag) search.delete("tag");
    else search.set("tag", tag);
    setSearch(search, { replace: true });
  };
  // Pages filtered down by the active tag chip. Recomputed each render so
  // the SVG and the page list always agree on what's visible.
  const visiblePages = useMemo(() => {
    if (!coverage) return [];
    if (!activeTag) return coverage.report.pages;
    return coverage.report.pages.filter((p) =>
      Array.isArray(p.tags) && p.tags.includes(activeTag),
    );
  }, [coverage, activeTag]);
  // Distinct tags across the (unfiltered) page set — drives the chip strip.
  const allTags = useMemo(() => {
    if (!coverage) return [];
    const set = new Set<string>();
    for (const p of coverage.report.pages) {
      for (const t of p.tags ?? []) set.add(t);
    }
    // Same sort order @uaip/coverage uses: smoke first, alphabetical after.
    return [...set].sort((a, b) => {
      if (a === "smoke" && b !== "smoke") return -1;
      if (b === "smoke" && a !== "smoke") return 1;
      return a.localeCompare(b);
    });
  }, [coverage]);

  // Shortcuts the main-content block reads to decide which empty state to
  // render. Keeping them here so the JSX below stays as a flat decision tree.
  const hasProjects = (projects?.length ?? 0) > 0;
  const hasCrawl = coverage ? coverage.context.crawlId !== null : false;
  const hasTraces = coverage ? coverage.context.traceCount > 0 : false;
  const hasPages = coverage ? coverage.report.pages.length > 0 : false;

  // Coverage is user-scoped — show a sign-in prompt (like /heals) instead of a
  // raw 401 when the visitor isn't authenticated.
  if (ready && !user) {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900 p-6 text-sm text-slate-300">
        <p className="font-medium text-slate-100">Sign in to view your Coverage Map</p>
        <p className="mt-1 text-slate-400">
          Coverage is computed from your own crawls and test runs — sign in to
          see which pages your suite covers, which are failing, and which are
          untested.
        </p>
        <Link
          to="/sign-in?redirect=/coverage"
          className="mt-4 inline-block rounded-md bg-indigo-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-400"
        >
          Sign in
        </Link>
      </div>
    );
  }

  return (
    <div>
      <header className="mb-6">
        <div className="mb-1 text-xs text-slate-500">
          <Link to="/" className="hover:text-slate-300">
            ← Dashboard
          </Link>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-slate-100">Coverage Map</h1>
            <p className="mt-1 max-w-xl text-sm text-slate-400">
              Which pages have passing tests, which are failing, and which the
              suite has never touched. Updates as new runs finish.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {projects && projects.length > 0 && projectId && (
              <ProjectPicker
                projects={projects}
                activeId={projectId}
                onPick={switchProject}
              />
            )}
            <WindowPicker value={days} onPick={pickDays} />
            {projectId && (
              <DownloadPdfButton
                projectId={projectId}
                days={days}
                // Only enable the export when the report has something
                // useful to render — empty PDFs make for confusing demos.
                enabled={
                  !!coverage &&
                  coverage.context.crawlId !== null &&
                  coverage.context.traceCount > 0
                }
              />
            )}
          </div>
        </div>
      </header>

      {/* Error banner (API failure) ───────────────────────────── */}
      {error && (
        <div className="mb-4 rounded-md border border-rose-700 bg-rose-900/40 px-4 py-3 text-sm text-rose-200">
          {error}
        </div>
      )}

      {/* Empty state: no projects at all yet ──────────────────── */}
      {projects !== null && !hasProjects && <NoProjectsEmpty />}

      {/* Loading state ────────────────────────────────────────── */}
      {projectId && !coverage && !error && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-8 text-center text-slate-400">
          Loading coverage…
        </div>
      )}

      {/* Main content ────────────────────────────────────────── */}
      {coverage && (
        <>
          {/* Three ways coverage can be "empty" — each gets a distinct
           *  explanation so the user isn't left wondering why 0%: */}
          {!hasCrawl ? (
            // Project exists but the on-disk crawl.json wasn't found.
            // Most likely the crawl hasn't finished, or we're looking at
            // the wrong project.
            <NoCrawlEmpty runCount={coverage.context.runCount} />
          ) : !hasPages ? (
            // Crawl exists but yielded zero pages (rare — corrupt crawl.json
            // or a one-page SPA with redirect loops). Treat like "no crawl".
            <NoCrawlEmpty runCount={coverage.context.runCount} />
          ) : !hasTraces ? (
            // The diagnostic 99% of users will hit on first visit: the
            // suite was generated but never run. Tell them EXACTLY how to
            // populate coverage instead of showing a silent 0%.
            <NoTracesEmpty
              crawlId={coverage.context.crawlId!}
              runCount={coverage.context.runCount}
              windowDays={coverage.context.windowDays}
            />
          ) : (
            <>
              <KpiStrip kpis={coverage.report.kpis} />
              <CoverageContextBar
                runCount={coverage.context.runCount}
                traceCount={coverage.context.traceCount}
                windowDays={coverage.context.windowDays}
                crawlId={coverage.context.crawlId}
              />
              <CriticalFlowsRail pages={coverage.report.pages} />
              {/* Plan §21 Phase 4 tag chip strip. Narrows BOTH the SVG
               *  and the page list to pages carrying the active tag. */}
              {allTags.length > 0 && (
                <TagFilter
                  tags={allTags}
                  active={activeTag}
                  onPick={pickTag}
                  totalCount={coverage.report.pages.length}
                  filteredCount={visiblePages.length}
                />
              )}
              {/* Plan Journey-5: "UAIP overlays the site graph with test
               *  coverage in green, gaps in red." Same SVG renderer as the
               *  Crawl Map, but with a status palette instead of category
               *  colors. */}
              <CoverageMapPanel
                flowGraph={coverage.flowGraph}
                pages={visiblePages}
              />
              <PageList pages={visiblePages} />
            </>
          )}
        </>
      )}
    </div>
  );
}

function clampDays(n: number): number {
  if (!Number.isFinite(n) || !Number.isInteger(n)) return 7;
  return Math.min(90, Math.max(1, n));
}

// ───────────────────────────────── KPIs ─────────────────────────────────

function KpiStrip({ kpis }: { kpis: CoverageKpis }): JSX.Element {
  // Journey 5 numbers the VP actually cares about.
  return (
    <section className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
      <KpiTile
        label="Pages tested"
        numerator={kpis.testedPages}
        denominator={kpis.totalPages}
        pct={kpis.pctPagesTested}
        tone="emerald"
        sub={`${kpis.passingPages} passing · ${kpis.failingPages} failing`}
      />
      <KpiTile
        label="Critical flows tested"
        numerator={kpis.criticalTested}
        denominator={kpis.criticalTotal}
        pct={kpis.pctCriticalFlowsTested}
        tone={kpis.pctCriticalFlowsTested === 100 ? "emerald" : "amber"}
        sub="login, cart, checkout, signup, payment"
      />
      <KpiTile
        label="Completely untested"
        numerator={kpis.untestedPages}
        denominator={kpis.totalPages}
        pct={kpis.pctUntested}
        tone="rose"
        sub="crawl pages with no matching test"
      />
    </section>
  );
}

function KpiTile({
  label,
  numerator,
  denominator,
  pct,
  tone,
  sub,
}: {
  label: string;
  numerator: number;
  denominator: number;
  pct: number;
  tone: "emerald" | "amber" | "rose";
  sub: string;
}): JSX.Element {
  const barColor =
    tone === "emerald"
      ? "bg-emerald-500"
      : tone === "amber"
        ? "bg-amber-400"
        : "bg-rose-500";
  const pctColor =
    tone === "emerald"
      ? "text-emerald-300"
      : tone === "amber"
        ? "text-amber-300"
        : "text-rose-300";
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
      <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className={`text-3xl font-semibold ${pctColor}`}>{pct}%</span>
        <span className="text-sm text-slate-500">
          {numerator} / {denominator}
        </span>
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-800">
        <div
          className={`h-full ${barColor}`}
          style={{ width: `${Math.max(2, pct)}%` }}
        />
      </div>
      <p className="mt-2 text-xs text-slate-500">{sub}</p>
    </div>
  );
}

// ────────────────────────────── Window picker ──────────────────────────────

// ──────────────────────── Project picker ────────────────────────

/**
 * Small <select> bound to the user's project list. Switching triggers a
 * route navigation to `/projects/:id/coverage` — we keep the URL as the
 * source of truth so bookmarks + back/forward both work.
 *
 * Why a native <select> instead of a custom dropdown: keyboard nav + mobile
 * touch target come for free, and the list is never more than a handful of
 * items (tier caps: 1 on Free, 3 on Starter, 10 on Pro).
 */
function ProjectPicker({
  projects,
  activeId,
  onPick,
}: {
  projects: ProjectSummary[];
  activeId: string;
  onPick: (id: string) => void;
}): JSX.Element {
  return (
    <label className="flex items-center gap-2 text-xs text-slate-400">
      <span className="sr-only">Project</span>
      <span className="px-2 text-slate-500">project</span>
      <select
        value={activeId}
        onChange={(e) => onPick(e.currentTarget.value)}
        className="rounded-md border border-slate-800 bg-slate-900/60 px-2 py-1 text-sm text-slate-100 focus:border-indigo-500 focus:outline-none"
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
    </label>
  );
}

// ────────────────────────── Empty states ──────────────────────────

function NoProjectsEmpty(): JSX.Element {
  return (
    <div className="rounded-xl border border-dashed border-slate-800 bg-slate-900/40 p-8 text-center text-slate-400">
      <p className="text-slate-200">No projects yet.</p>
      <p className="mt-1 text-sm">
        Run a crawl from the{" "}
        <Link to="/crawls/new" className="text-indigo-300 underline">
          New crawl
        </Link>{" "}
        tab — UAIP auto-creates a project per target host.
      </p>
    </div>
  );
}

function NoCrawlEmpty({ runCount }: { runCount: number }): JSX.Element {
  return (
    <div className="rounded-xl border border-dashed border-slate-800 bg-slate-900/40 p-8 text-center text-slate-400">
      <p className="text-slate-200">No crawl for this project yet.</p>
      <p className="mt-1 text-sm">
        Coverage needs a crawl's page list to report on. Start a crawl from the{" "}
        <Link to="/crawls/new" className="text-indigo-300 underline">
          New crawl
        </Link>{" "}
        tab or pick a different project from the picker above.
      </p>
      {runCount > 0 && (
        <p className="mt-2 text-xs text-slate-500">
          ({runCount} run{runCount === 1 ? "" : "s"} exist for this project but
          no crawl.json was found on disk.)
        </p>
      )}
    </div>
  );
}

/**
 * Shown when the project has a crawl (so we know the page list) but no
 * `run_traces` rows in the selected window. This is the thing nearly every
 * first-time user hits — they crawled, then opened /coverage expecting the
 * map to magically populate without executing the suite.
 *
 * We resist the temptation to silently show 0% green and instead teach:
 *   - what's missing (test runs that wrote a trace)
 *   - the exact command to fix it (`uaip run --trace …`)
 *   - where to find the generated tests on disk (the Tests tab)
 */
function NoTracesEmpty({
  crawlId,
  runCount,
  windowDays,
}: {
  crawlId: string;
  runCount: number;
  windowDays: number;
}): JSX.Element {
  // Mirror of the Tests-tab "Run with trace" button so the user doesn't have
  // to navigate away. Same status state machine + same auto-redirect to the
  // trace viewer. The CLI snippet stays as a fallback for users who'd rather
  // wire it into their CI.
  const navigate = useNavigate();
  const [runStatus, setRunStatus] = useState<
    "idle" | "starting" | "running" | "done" | "failed"
  >("idle");
  const [runError, setRunError] = useState<string | null>(null);
  const busy = runStatus === "starting" || runStatus === "running";

  async function startRun(): Promise<void> {
    setRunStatus("starting");
    setRunError(null);
    try {
      const { run } = await runTestsForCrawl(crawlId);
      setRunStatus("running");
      await waitForRunFinish(run.id);
      setRunStatus("done");
      navigate(`/runs/${encodeURIComponent(run.id)}/trace`);
    } catch (err) {
      setRunError(err instanceof Error ? err.message : String(err));
      setRunStatus("failed");
    }
  }

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6">
      <div className="flex items-center gap-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500/15 text-lg text-amber-300">
          !
        </span>
        <div>
          <h2 className="text-base font-semibold text-slate-100">
            No test runs yet for this project
          </h2>
          <p className="text-sm text-slate-400">
            Coverage is computed from test executions, not from crawls.
          </p>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={startRun}
          disabled={busy}
          className="rounded-md bg-emerald-500 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-400 disabled:opacity-50"
        >
          {runStatus === "starting"
            ? "Starting…"
            : runStatus === "running"
              ? "Running…"
              : runStatus === "done"
                ? "Opening trace…"
                : "Run with trace now"}
        </button>
        <span className="text-xs text-slate-500">
          Spawns <code className="rounded bg-slate-800 px-1">uaip run --trace</code>{" "}
          server-side, then opens the trace viewer when it finishes.
        </span>
      </div>
      {runError && (
        <p className="mt-2 text-xs text-rose-300">
          Run failed to start: {runError}
        </p>
      )}

      <div className="mt-5 rounded-md border border-slate-800 bg-slate-950/60 p-4">
        <p className="text-xs uppercase tracking-wide text-slate-400">
          Or run from your terminal / CI
        </p>
        <pre className="mt-2 overflow-x-auto rounded bg-slate-900 px-3 py-2 text-xs text-slate-200">
{`pnpm uaip run ./${crawlId} --trace ./trace-out`}
        </pre>
        <p className="mt-2 text-xs text-slate-400">
          The <code className="rounded bg-slate-800 px-1">--trace</code> flag is
          what writes the <code className="rounded bg-slate-800 px-1">run_traces</code>
          {" "}row that coverage reads. Without it the run completes but the Coverage
          Map stays empty.
        </p>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 text-xs text-slate-400">
        <Link
          to={`/crawls/${encodeURIComponent(crawlId)}/tests`}
          className="rounded-md border border-slate-700 px-3 py-1.5 hover:bg-slate-800"
        >
          View generated tests →
        </Link>
        <Link
          to={`/crawls/${encodeURIComponent(crawlId)}`}
          className="rounded-md border border-slate-700 px-3 py-1.5 hover:bg-slate-800"
        >
          Open crawl map →
        </Link>
        <Link
          to="/runs"
          className="rounded-md border border-slate-700 px-3 py-1.5 hover:bg-slate-800"
        >
          Runs history
        </Link>
      </div>

      <p className="mt-4 text-xs text-slate-500">
        Looked for traces in the last{" "}
        {windowDays === 1 ? "24 hours" : `${windowDays} days`}
        {runCount > 0
          ? ` — found ${runCount} run${runCount === 1 ? "" : "s"} without a trace.`
          : " — no runs yet."}
      </p>
    </div>
  );
}

// ────────────────────────── Download PDF ──────────────────────────

/**
 * Plan Journey 5: "Priya exports the map as a PDF for the VP."
 *
 * Plain `<a href download>` so the browser handles Content-Disposition for us
 * — same pattern as the Tests-tab Download-ZIP button. When `enabled` is
 * false (no crawl, no traces) we render a non-clickable styled span so the
 * affordance is still visible but the user gets a tooltip explaining why.
 */
function DownloadPdfButton({
  projectId,
  days,
  enabled,
}: {
  projectId: string;
  days: number;
  enabled: boolean;
}): JSX.Element {
  const baseCls =
    "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium";
  if (!enabled) {
    return (
      <span
        className={`${baseCls} cursor-not-allowed border-slate-800 bg-slate-900/40 text-slate-500`}
        title="Run the suite with --trace first to populate coverage, then export."
      >
        Download PDF
      </span>
    );
  }
  return (
    <a
      href={coveragePdfUrl(projectId, days)}
      // The server's Content-Disposition wins over this — but it's a fallback
      // for browsers that ignore the header for some reason.
      download
      className={`${baseCls} border-indigo-700 bg-indigo-500/15 text-indigo-200 hover:bg-indigo-500/25`}
    >
      Download PDF
    </a>
  );
}

function WindowPicker({
  value,
  onPick,
}: {
  value: number;
  onPick: (d: number) => void;
}): JSX.Element {
  const options = [1, 7, 30, 90];
  return (
    <div className="flex items-center gap-1 rounded-md border border-slate-800 bg-slate-900/60 p-1 text-xs">
      <span className="px-2 text-slate-500">window</span>
      {options.map((d) => (
        <button
          key={d}
          type="button"
          onClick={() => onPick(d)}
          className={`rounded px-2 py-1 ${
            value === d
              ? "bg-indigo-500/20 text-indigo-200"
              : "text-slate-300 hover:bg-slate-800"
          }`}
        >
          {d === 1 ? "24h" : `${d}d`}
        </button>
      ))}
    </div>
  );
}

// ───────────────────────── Context bar ─────────────────────────

function CoverageContextBar({
  runCount,
  traceCount,
  windowDays,
  crawlId,
}: {
  runCount: number;
  traceCount: number;
  windowDays: number;
  crawlId: string | null;
}): JSX.Element {
  return (
    <div className="mb-6 rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3 text-xs text-slate-400">
      Based on{" "}
      <span className="text-slate-200 font-medium">{traceCount}</span> run
      {traceCount === 1 ? "" : "s"} with traces (of {runCount} total) in the last{" "}
      {windowDays === 1 ? "24 hours" : `${windowDays} days`}
      {crawlId && (
        <>
          , against crawl{" "}
          <Link
            to={`/crawls/${encodeURIComponent(crawlId)}`}
            className="font-mono text-indigo-300 hover:text-indigo-200"
          >
            {crawlId}
          </Link>
        </>
      )}
      .
    </div>
  );
}

// ─────────────────────── Critical flows rail ───────────────────────

/**
 * Why critical flows get their own strip: Journey 5 specifically pulls out
 * "critical flows tested" as a headline number. Showing only the critical
 * pages with their status in a compact rail means a VP skimming the report
 * can see "login: green, checkout: red" without scrolling through 60 pages.
 */
function CriticalFlowsRail({ pages }: { pages: CoveragePageRow[] }): JSX.Element | null {
  const crit = pages.filter((p) => p.isCritical);
  if (crit.length === 0) return null;
  return (
    <section className="mb-6">
      <h2 className="mb-2 text-sm font-semibold text-slate-200">
        Critical flows
      </h2>
      <div className="flex flex-wrap gap-2">
        {crit.map((p) => (
          <div
            key={p.url}
            className={`rounded-md border px-3 py-2 text-sm ${criticalChipClass(p.status)}`}
          >
            <span className="font-medium">{p.label || labelFromUrl(p.url)}</span>
            <span className="ml-2 text-xs opacity-80">
              {p.status === "passed"
                ? `${p.passingTests} passing`
                : p.status === "failed"
                  ? `${p.failingTests} failing`
                  : "no test"}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function criticalChipClass(status: CoveragePageRow["status"]): string {
  if (status === "passed")
    return "border-emerald-700 bg-emerald-900/30 text-emerald-200";
  if (status === "failed")
    return "border-rose-700 bg-rose-900/30 text-rose-200";
  return "border-slate-700 bg-slate-900/50 text-slate-400";
}

// ─────────────────────────── Page list ───────────────────────────

function PageList({ pages }: { pages: CoveragePageRow[] }): JSX.Element {
  // Sort: failing first (most actionable), then untested, then passed.
  // Inside each bucket, critical flows float to the top.
  const ordered = useMemo(() => {
    const bucket = (p: CoveragePageRow): number =>
      p.status === "failed" ? 0 : p.status === "untested" ? 1 : 2;
    return [...pages].sort((a, b) => {
      const ba = bucket(a);
      const bb = bucket(b);
      if (ba !== bb) return ba - bb;
      if (a.isCritical !== b.isCritical) return a.isCritical ? -1 : 1;
      return a.url.localeCompare(b.url);
    });
  }, [pages]);

  if (ordered.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-800 bg-slate-900/40 p-8 text-center text-slate-400">
        <p>No pages in the latest crawl yet.</p>
        <p className="mt-1 text-sm">
          Run a crawl from the{" "}
          <Link to="/crawls/new" className="text-indigo-300 underline">
            New crawl
          </Link>{" "}
          tab — UAIP will map the site here.
        </p>
      </div>
    );
  }

  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold text-slate-200">
        All pages ({ordered.length})
      </h2>
      <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900/40">
        <ul className="divide-y divide-slate-800">
          {ordered.map((p) => (
            <li key={p.url} className="flex items-center gap-3 px-4 py-3">
              <StatusDot status={p.status} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-200">
                    {p.label || labelFromUrl(p.url)}
                  </span>
                  {p.isCritical && (
                    <span className="rounded border border-amber-700 bg-amber-900/30 px-1.5 text-[10px] uppercase tracking-wide text-amber-300">
                      critical
                    </span>
                  )}
                </div>
                <div className="mt-0.5 truncate font-mono text-xs text-slate-500">
                  {p.url}
                </div>
              </div>
              <div className="shrink-0 text-right text-xs text-slate-400">
                <div>
                  <span className="text-emerald-300">{p.passingTests} ✓</span>
                  {p.failingTests > 0 && (
                    <>
                      {" · "}
                      <span className="text-rose-300">{p.failingTests} ✗</span>
                    </>
                  )}
                  {p.passingTests === 0 && p.failingTests === 0 && (
                    <span className="text-slate-500">untested</span>
                  )}
                </div>
                <div className="mt-0.5 font-mono text-[10px] text-slate-600">
                  {p.expectedClassName}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function StatusDot({ status }: { status: CoveragePageRow["status"] }): JSX.Element {
  const cls =
    status === "passed"
      ? "bg-emerald-400"
      : status === "failed"
        ? "bg-rose-400"
        : "bg-slate-600";
  return <span className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${cls}`} />;
}

/** Fallback label when the crawl page has no semantic label (unknown pages,
 *  auth walls, etc). Matches the legible-ish path-last-segment rule the
 *  compute layer uses — just for display. */
function labelFromUrl(raw: string): string {
  try {
    const u = new URL(raw);
    const parts = u.pathname.split("/").filter((p) => p.length > 0);
    if (parts.length === 0) return u.host;
    const last = parts[parts.length - 1]!.replace(/\.[^.]+$/, "");
    return last || u.host;
  } catch {
    return raw;
  }
}

// ────────────────────────── Coverage Map panel ──────────────────────────

/**
 * Plan Journey 5: "Priya clicks 'Coverage Map.' UAIP overlays the site graph
 * with test coverage in green, gaps in red."
 *
 * Reuses the same FlowGraphSvg renderer the /crawls/:id Crawl Map uses, but
 * passes a colorByUrl callback that maps each node's URL to its coverage
 * status (resolved from the page list). Empty graph (no labelled pages) →
 * explanatory empty state, same shape the CrawlMap uses for parity.
 */
function CoverageMapPanel({
  flowGraph,
  pages,
}: {
  flowGraph: CoverageReportResponse["flowGraph"];
  pages: CoveragePageRow[];
}): JSX.Element {
  // Build the URL→status lookup once per render. Same URL might appear
  // twice in pages[] in pathological cases, but we already dedupe upstream
  // in extractPagesForCoverage so first wins is safe.
  const statusByUrl = useMemo(() => {
    const m = new Map<string, CoverageNodeStatus>();
    for (const p of pages) {
      m.set(p.url, p.status as CoverageNodeStatus);
    }
    return m;
  }, [pages]);

  if (!flowGraph || flowGraph.nodes.length === 0) {
    return (
      <section className="mb-6">
        <h2 className="mb-2 text-sm font-semibold text-slate-200">Coverage map</h2>
        <div className="rounded-xl border border-dashed border-slate-800 bg-slate-900/40 p-6 text-sm text-slate-500">
          No labelled pages to graph yet. The crawl needs at least one page
          the rule registry recognises (login, cart, checkout, etc.) before
          we can draw the site graph here.
        </div>
      </section>
    );
  }

  const passing = flowGraph.nodes.filter(
    (n) => statusByUrl.get(n.url) === "passed",
  ).length;
  const failing = flowGraph.nodes.filter(
    (n) => statusByUrl.get(n.url) === "failed",
  ).length;
  const untested = flowGraph.nodes.length - passing - failing;

  return (
    <section className="mb-6">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-200">Coverage map</h2>
        <Legend passing={passing} failing={failing} untested={untested} />
      </div>
      <div className="rounded-xl border border-slate-800 bg-slate-900/50">
        <div className="overflow-auto p-2">
          <FlowGraphSvg
            graph={flowGraph}
            colorByUrl={(url) => statusByUrl.get(url) ?? "untested"}
          />
        </div>
      </div>
    </section>
  );
}

function Legend({
  passing,
  failing,
  untested,
}: {
  passing: number;
  failing: number;
  untested: number;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-400">
      <Swatch color="bg-emerald-500" label={`${passing} passing`} />
      <Swatch color="bg-rose-500" label={`${failing} failing`} />
      <Swatch color="bg-slate-500" label={`${untested} untested`} />
    </div>
  );
}

function Swatch({ color, label }: { color: string; label: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`h-2 w-2 rounded-sm ${color}`} />
      <span>{label}</span>
    </span>
  );
}

// ────────────────────────── Tag filter ──────────────────────────

/**
 * Plan §21 Phase 4 tag chip strip. Renders one chip per distinct tag in
 * the report, plus an "All" reset chip. Active chip highlighted in
 * indigo. Click → write `?tag=<value>` to the URL (so refresh + deep
 * link both work) and the parent filters the map + page list down to
 * pages carrying that tag.
 *
 * Filtered-out pages are dropped from the page list and show as slate
 * (untested-tone) on the SVG since the colorByUrl callback returns
 * "untested" for any URL not in the visible set. That's good enough for
 * v1; v2 could fade them further or hide them entirely.
 */
function TagFilter({
  tags,
  active,
  onPick,
  totalCount,
  filteredCount,
}: {
  tags: string[];
  active: string;
  onPick: (tag: string) => void;
  totalCount: number;
  filteredCount: number;
}): JSX.Element {
  return (
    <section className="mb-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-slate-500 mr-1">
          tag
        </span>
        <Chip label="All" active={active === ""} onClick={() => onPick("")} />
        {tags.map((t) => (
          <Chip
            key={t}
            label={t}
            active={active === t}
            onClick={() => onPick(t)}
          />
        ))}
        {active && (
          <span className="ml-1 text-[11px] text-slate-500">
            showing {filteredCount} of {totalCount}
          </span>
        )}
      </div>
    </section>
  );
}

function Chip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  const cls = active
    ? "border-indigo-500 bg-indigo-500/15 text-indigo-200"
    : "border-slate-700 bg-slate-900/40 text-slate-300 hover:bg-slate-800";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs font-medium ${cls}`}
    >
      {label}
    </button>
  );
}
